import React, { useEffect, useMemo, useState } from "react";
import LayerAdmin from "../LayerAdmin";
import PaginationSearch from "../components/PaginationAndSearch";
import EmployeeTable from "../components/EmployeeTable";
import { getAllEmpolyees, clearBECache } from "../api/Employees";
import { useNavigate, useLocation } from "react-router-dom";
import URL_MAPPING from "../../../routes/constants";
import { toast, ToastContainer } from "react-toastify";
import { useDebounce } from "../hooks/debounce";
import { CustomModal } from "../components/CustomModal";

const Admins = () => {
  const [tableData, setTableData] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [showClearCacheModal, setShowClearCacheModal] = useState(false);
  const [clearCacheLoading, setClearCacheLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isPlanning = location.pathname.includes("planning") || localStorage.getItem("section") === "Planning";

  const debounce = useDebounce(search);

  const getTableData = async () => {
    try {
      setLoading(true);
      const paramsObject: {
        search?: string;
      } = {};
      if (search.length > 0) paramsObject.search = search;
      const response = await getAllEmpolyees(page, 20, paramsObject);
      if (!response || !Array.isArray(response.items)) {
        toast.error("No data received or malformed response");
        console.warn("No data received or malformed response:", response);
        setTableData({ items: [], page: 1, size: 20, total: 0 });
        return;
      }
      setTableData(response);
      setPage(response.page ?? 1);
    } catch (error: any) {
      console.error("Failed to fetch employee data:", error);
      if (error?.response?.status === 404) {
        toast.error("Route not found, Please try again later");
      } else if (error?.response?.status === 500) {
        toast.error("Error while fetching the employee");
      } else {
        const detail = error?.response?.data?.detail;
        if (Array.isArray(detail)) {
          detail.forEach((err) => toast.error(err.msg));
        } else {
          toast.error(detail?.msg || detail || "An unexpected error occurred.");
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClearBECache = async () => {
    try {
      setClearCacheLoading(true);
      const response = await clearBECache();
      toast.success(response?.message || "BE Cache cleared successfully");
    } catch (error: any) {
      console.error("Failed to clear BE cache:", error);
      if (error?.response?.status === 404) {
        toast.error("Route not found, Please try again later");
      } else if (error?.response?.status === 500) {
        toast.error("Error while clearing BE cache");
      } else {
        const detail = error?.response?.data?.detail;
        if (Array.isArray(detail)) {
          detail.forEach((err) => toast.error(err.msg));
        } else {
          toast.error(
            detail?.msg || detail || error?.message || "An unexpected error occurred."
          );
        }
      }
    } finally {
      setClearCacheLoading(false);
      setShowClearCacheModal(false);
    }
  };

  const handlePaginationNext = () => {
    if (loading) return;
    setPage((prev) => prev + 1);
  };

  const handlePaginationPrev = () => {
    if (loading) return;
    setPage((prev) => Math.max(prev - 1, 1));
  };

  const handleEditEmoloyee = (id: string) => {
    if (isPlanning) {
      navigate(URL_MAPPING.PLANNING_EDITEMPLOYEE(id));
    } else {
      navigate(URL_MAPPING.EDITEMPLOYEE(id));
    }
  };

  const totalPages = useMemo(() => {
    if (!tableData?.total || !tableData?.size) return 0;
    return Math.ceil(tableData.total / tableData.size);
  }, [tableData?.total, tableData?.size]);

  useEffect(() => {
    getTableData();
  }, [page, debounce]);
  return (
    <LayerAdmin>
      <ToastContainer />
      <PaginationSearch
        page={page}
        size={totalPages}
        handlePaginationNext={handlePaginationNext}
        handlePaginationPrev={handlePaginationPrev}
        search={search}
        placeholder="Search By Employee ID"
        setSearch={setSearch}
      />
      <div className="flex items-center justify-between px-5 pb-5 gap-3">
        <h3 className="text-xl font-bold text-gray-700">Employee</h3>
        {!isPlanning && (
          <button
            onClick={() => setShowClearCacheModal(true)}
            className="bg-teal-500 text-white px-4 py-2 rounded hover:bg-teal-600 font-medium text-sm transition-colors flex items-center gap-2"
          >
            Clear BE cache
          </button>
        )}
      </div>
      <EmployeeTable
        tableData={tableData}
        loading={loading}
        handleEditEmoloyee={handleEditEmoloyee}
      />
      <CustomModal
        isOpen={showClearCacheModal}
        onClose={() => setShowClearCacheModal(false)}
        onConfirm={handleClearBECache}
        title="Clear BE Cache"
        message="Are you sure you want to clear the BE cache?"
        confirmText="Confirm"
        cancelText="Cancel"
        loading={clearCacheLoading}
      />
    </LayerAdmin>
  );
};

export default Admins;
